import { Component, ElementRef, EventEmitter, inject, Input, OnChanges, Output, SimpleChanges, ViewChild, ChangeDetectionStrategy } from '@angular/core'
import { Meeting, MeetingListType, MeetingsService } from '../meetings.service'
import { SpinnerComponent } from '../spinner/spinner.component'
import { TranslateDirective, TranslatePipe, TranslateService } from '@ngx-translate/core'
import { MatDialog } from '@angular/material/dialog'
import { PreviousMeetingCardComponent } from '../previous-meeting-card/previous-meeting-card.component'
import { UpcomingMeetingCardComponent } from '../upcoming-meeting-card/upcoming-meeting-card.component'
import { OngoingMeetingCardComponent } from '../ongoing-meeting-card/ongoing-meeting-card.component'
import { AsyncPipe, DatePipe } from '@angular/common'
import { addDays, isBefore, isSameDay, startOfDay, subDays } from 'date-fns'
import { ActivatedRoute, Router } from '@angular/router'
import { BehaviorSubject, combineLatest, distinctUntilChanged, filter, map } from 'rxjs'
import { MatSlideToggle } from '@angular/material/slide-toggle'
import { FormsModule } from '@angular/forms'
import { MatTooltip } from '@angular/material/tooltip'
import { WaIntersectionObservee, WaIntersectionObserverDirective } from '@ng-web-apis/intersection-observer'

@Component({
  selector: 'app-meeting-list',
  standalone: true,
  imports: [
    AsyncPipe,
    SpinnerComponent,
    PreviousMeetingCardComponent,
    UpcomingMeetingCardComponent,
    OngoingMeetingCardComponent,
    TranslatePipe,
    TranslateDirective,
    MatSlideToggle,
    FormsModule,
    MatTooltip,
    WaIntersectionObservee,
    WaIntersectionObserverDirective,
  ],
  providers: [DatePipe],
  templateUrl: './meeting-list.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './meeting-list.component.scss',
})
export class MeetingListComponent implements OnChanges {
  @Input()
  meetings!: Meeting[]

  @Input()
  hasMore!: boolean

  @Input()
  isFetchingNextPage!: boolean

  @Input()
  meetingListType!: MeetingListType

  @Input()
  useGroupingByDate = false

  @Input()
  activeMatch: string | null = null

  @Input()
  activeMeeting: Meeting | null = null

  @Output()
  fetchNextPage = new EventEmitter<void>()

  @Output()
  hoverMeeting = new EventEmitter<Meeting>()

  @Output()
  meetingGotIntoView = new EventEmitter<number>()

  @ViewChild('meetingsContainer')
  meetingsContainer!: ElementRef

  router = inject(Router)
  route = inject(ActivatedRoute)
  meetingsService = inject(MeetingsService)
  dialog = inject(MatDialog)
  translateService = inject(TranslateService)
  datePipe = inject(DatePipe)

  showOnlyToBeJoinedMeetings$ = this.route.queryParams.pipe(
    map((params) => params['showOnlyToBeJoined'] === 'true'),
    distinctUntilChanged()
  )
  groupedMeetings$ = new BehaviorSubject<{ date: string; meetings: Meeting[] }[]>([])
  ongoingMeetings$ = this.meetingsService.getOngoingMeetings().pipe(
    filter((result) => result.isSuccess),
    map((result) => result.data || [])
  )

  filteredGroupedMeetings$ = combineLatest([this.showOnlyToBeJoinedMeetings$, this.groupedMeetings$]).pipe(
    map(([showOnlyToBeJoinedMeetings, groupedMeetings]) => {
      if (!showOnlyToBeJoinedMeetings) {
        return groupedMeetings
      }
      return groupedMeetings
        .map((group) => ({
          ...group,
          meetings: group.meetings.filter((meeting) => meeting.isSetToJoin || meeting.assistantInstructedToJoinByOtherUsers),
        }))
        .filter((group) => group.meetings.length > 0)
    })
  )

  onMeetingVisible(entries: IntersectionObserverEntry[], meeting: Meeting) {
    const containerRect = this.meetingsContainer.nativeElement.getBoundingClientRect()
    entries.forEach((entry) => {
      const isAtTopEdge = entry.boundingClientRect.top <= containerRect.top
      if (isAtTopEdge && entry.intersectionRatio > 0) {
        const currentMeetingId = this.route.snapshot.queryParamMap.get('meetingId')
        if (currentMeetingId === null || meeting.id !== parseInt(currentMeetingId)) {
          this.meetingGotIntoView.emit(meeting.id)
        }
      }
    })
  }

  scrollToMeeting(meetingId: number) {
    const meetingElement = this.meetingsContainer.nativeElement.querySelector(`[data-meeting-id="${meetingId}"]`)
    if (meetingElement) {
      meetingElement.scrollIntoView({ block: 'start' })
    } else {
      console.warn(`Meeting element not found for id ${meetingId}. Skipping scrolling.`)
    }
  }

  loadMore() {
    if (this.isFetchingNextPage) {
      return
    }
    this.fetchNextPage.emit()
  }

  toggleShowOnlyToBeJoinedMeetings(value: boolean) {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { showOnlyToBeJoined: value },
      queryParamsHandling: 'merge',
    })
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['meetings']) {
      this.groupedMeetings$.next(this.groupAndSortMeetingsByDay(this.meetings))
    }
  }

  formatUpcomingDate(date: Date): string {
    const now = new Date()
    if (isSameDay(date, now)) {
      return this.translateService.instant('COMMON.LATER_TODAY')
    }
    if (isSameDay(date, addDays(now, 1))) {
      return this.translateService.instant('COMMON.TOMORROW')
    }
    // If the date is somehow in the past (e.g. due to timezone issues), show "Today" as a fallback
    if (isBefore(date, startOfDay(now))) {
      return this.translateService.instant('COMMON.LATER_TODAY')
    }
    return this.datePipe.transform(date, 'dd MMMM', 'UTC') as string
  }

  formatPreviousDate(date: Date): string {
    const now = new Date()
    if (isSameDay(date, now)) {
      return this.translateService.instant('COMMON.TODAY')
    }
    if (isSameDay(date, addDays(now, 1))) {
      return this.translateService.instant('COMMON.TOMORROW')
    }
    if (isSameDay(date, subDays(now, 1))) {
      return this.translateService.instant('COMMON.YESTERDAY')
    }
    return this.datePipe.transform(date, 'EEEE, dd MMMM', 'UTC') as string
  }

  groupAndSortMeetingsByDay(meetings: Meeting[]): { date: string; meetings: Meeting[] }[] {
    const groupedMeetings = Object.groupBy(meetings, (meeting) => {
      const date = new Date(meeting.date)
      return date.toISOString().split('T')[0]
    }) as Required<{ [date: string]: Meeting[] }>
    return Object.entries(groupedMeetings)
      .toSorted(([dateA], [dateB]) =>
        // For upcoming meetings we want the nearest future date first, for previous meetings we want the nearest past date first
        this.meetingListType === 'upcoming' ? new Date(dateA).getTime() - new Date(dateB).getTime() : new Date(dateB).getTime() - new Date(dateA).getTime()
      )
      .map(([date, meetings]) => ({
        date: this.meetingListType === 'upcoming' ? this.formatUpcomingDate(new Date(date)) : this.formatPreviousDate(new Date(date)),
        meetings,
      }))
  }
}
